import { client } from '@/client';
import {
  AwarenessCompletions,
  CourseScoreByTagReport,
  GroupedOverdueCoursesReport,
  ListCompletionsOverTimeReportResponse,
  ListOverdueUsersReportResponse,
  PhishingClickRate,
  ProductActivityOvertimeReport,
  SCSurveyCompletions,
  SCSurveyScore,
} from '@/client/dashboard/types';
import { GroupedByEntityFilterType, ReportRequest } from '@/client/reports';
import { TagsRequest } from '@/client/tags';
import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';

export const useCourseCompletionsReports = (
  accountId: string,
  params: UseQueryOptions & ReportRequest = {},
): UseQueryResult<AwarenessCompletions[]> & {
  courseCompletions: AwarenessCompletions[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['courseCompletionsReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.dashboard.getCourseCompletionsReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    courseCompletions: data,
    ...(rest as UseQueryResult<AwarenessCompletions[]>),
  };
};

export const usePhishingClickRateReports = (
  accountId: string,
  params: UseQueryOptions & ReportRequest = {},
): UseQueryResult<PhishingClickRate[]> & {
  phishingClickRate: PhishingClickRate[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['phishingClickRateReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.dashboard.getPhishingClickRateReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    phishingClickRate: data,
    ...(rest as UseQueryResult<PhishingClickRate[]>),
  };
};

export const useSCSurveyCompletionsReports = (
  accountId: string,
  params: UseQueryOptions & ReportRequest = {},
): UseQueryResult<SCSurveyCompletions[]> & {
  scSurveyCompletions: SCSurveyCompletions[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['scSurveyCompletionsReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.dashboard.getSCSurveyCompletionsReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    scSurveyCompletions: data,
    ...(rest as UseQueryResult<SCSurveyCompletions[]>),
  };
};

export const useSCSurveyScoreReports = (
  accountId: string,
  params: UseQueryOptions & ReportRequest = {},
): UseQueryResult<SCSurveyScore[]> & {
  scSurveyScore: SCSurveyScore[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['scSurveyScoreReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.dashboard.getSCSurveyScoreReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    scSurveyScore: data,
    ...(rest as UseQueryResult<SCSurveyScore[]>),
  };
};

export const useProductActivityOverTimeReports = (
  accountId: string,
  params: UseQueryOptions & ReportRequest = {},
): UseQueryResult<ProductActivityOvertimeReport[]> & {
  productActivityOverTime: ProductActivityOvertimeReport[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['productActivityOverTimeReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.dashboard.getProductActivityOverTimeReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    productActivityOverTime: data,
    ...(rest as UseQueryResult<ProductActivityOvertimeReport[]>),
  };
};

export const useUsersWithOverdueCoursesReports = (
  accountId: string,
  params: UseQueryOptions & ReportRequest = {},
): UseQueryResult<ListOverdueUsersReportResponse> & {
  usersWithOverdueCourses: ListOverdueUsersReportResponse | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['usersWithOverdueCoursesReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.dashboard.getUsersWithOverdueCoursesReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    usersWithOverdueCourses: data,
    ...(rest as UseQueryResult<ListOverdueUsersReportResponse>),
  };
};

export const useCompletionsOverTimeReports = (
  accountId: string,
  params: UseQueryOptions & ReportRequest = {},
): UseQueryResult<ListCompletionsOverTimeReportResponse> & {
  completionsOverTime: ListCompletionsOverTimeReportResponse | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['completionsOverTimeReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.dashboard.getCompletionsOverTimeReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    completionsOverTime: data,
    ...(rest as UseQueryResult<ListCompletionsOverTimeReportResponse>),
  };
};

// AWT dashboard
export const useGroupedOverdueCoursesReports = (
  accountId: string,
  params: UseQueryOptions &
    ReportRequest & {
      groupBy: GroupedByEntityFilterType;
    },
): UseQueryResult<GroupedOverdueCoursesReport[]> & {
  groupedOverdueCourses: GroupedOverdueCoursesReport[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['groupedOverdueCoursesReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.dashboard.getGroupedOverdueCoursesReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    groupedOverdueCourses: data,
    ...(rest as UseQueryResult<GroupedOverdueCoursesReport[]>),
  };
};

export const useCourseScoreByTagReports = (
  accountId: string,
  params: UseQueryOptions & ReportRequest & TagsRequest = {},
): UseQueryResult<CourseScoreByTagReport[]> & {
  courseScoreByTag: CourseScoreByTagReport[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['courseScoreByTagReports', accountId, JSON.stringify(params)],
    () =>
      accountId
        ? client.dashboard.getCourseScoreByTagReport(accountId, params)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    courseScoreByTag: data,
    ...(rest as UseQueryResult<CourseScoreByTagReport[]>),
  };
};
